import React, { useState } from 'react';
import axios from 'axios';
import { Form, Field } from 'react-final-form';
import { Column, Card, Flex, Row, Button } from '../../../../design-system';
import { isExpired, currencyFormatter } from '../../../../helpers/utils';
import Alert from '../../../../helpers/alert';
import { successRefetch } from '../../../../helpers/data-loader';
import AttachmentsBase from '../../../../components/Attachments';
import { ExpiredCard } from '../../../../components/Expired';
import FieldColumn from '../../../../components/FieldColumn';
import FieldText from '../../../../components/FieldText';
import CardHeader from '../../../../components/CardHeader';
import FIELDS from './constants';
import { ERR_MSG } from '../../../../globalConstants';
import DateField from '../../../../components/DateField';

const Attachments = AttachmentsBase as React.ComponentType<any>;

interface FiscalRegulatoryProps {
  data: { id: any; fiscal: any; all_files?: any[]; updated_at?: any };
  refetch: () => void;
  canEdit: boolean;
}

const FiscalRegulatory = ({
  data: { id, fiscal, all_files, updated_at: updatedAt },
  refetch,
  canEdit,
}: FiscalRegulatoryProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isAttachmentsModalOpen, toggleAttachmentsModal] = useState(false);

  const handleFiscalUpdate = async (values) => {
    const { current_budget, prior_budget, future_budget } = values;
    try {
      await axios.put(`/client/${id}/fiscal/${fiscal.id}`, {
        ...values,
        current_budget:
          current_budget && current_budget.replace(/[^0-9.]/g, ''),
        prior_budget: prior_budget && prior_budget.replace(/[^0-9.]/g, ''),
        future_budget: future_budget && future_budget.replace(/[^0-9.]/g, ''),
      });
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  const { current_budget, prior_budget, future_budget } = fiscal;
  return (
    <>
      <ExpiredCard expired={isExpired(fiscal.expires)}>
        <CardHeader
          title="Fiscal and Regulatory"
          onEditChange={setIsEditing}
          isEditing={isEditing}
          canEdit={canEdit}
          updatedAt={updatedAt}
        />
        <Card.Body>
          {isEditing ? (
            <Flex>
              <Form
                onSubmit={handleFiscalUpdate}
                initialValues={{
                  ...fiscal,
                  current_budget:
                    current_budget && currencyFormatter.format(current_budget),
                  prior_budget:
                    prior_budget && currencyFormatter.format(prior_budget),
                  future_budget:
                    future_budget && currencyFormatter.format(future_budget),
                }}
                render={({ handleSubmit, submitting, pristine }) => (
                  <form onSubmit={handleSubmit}>
                    <Row>
                      {FIELDS.map((field) => {
                        if (field.type === 'date') {
                          return (
                            <Column key={field.name} pb={2} width={300}>
                              <Field name={field.name}>
                                {({ input }) => {
                                  return (
                                    <DateField
                                      name={field.name}
                                      label={field.label}
                                      selected={input.value}
                                      onChange={(value) =>
                                        input.onChange(value)
                                      }
                                      showPastDue={false}
                                    />
                                  );
                                }}
                              </Field>
                            </Column>
                          );
                        }

                        return (
                          <FieldColumn
                            key={field.name}
                            width={300}
                            {...field}
                          />
                        );
                      })}
                    </Row>
                    <Button.Group mt={20} justifyContent="flex-end">
                      <Button
                        id="exit-edit-button"
                        type="submit"
                        disabled={submitting || pristine}
                      >
                        Save
                      </Button>
                    </Button.Group>
                  </form>
                )}
              />
            </Flex>
          ) : (
            <>
              <Row>
                {FIELDS.map((field) => {
                  return (
                    <Column key={field.name} mt={2} pb={3} width={300}>
                      <FieldText data={fiscal} {...field} />
                    </Column>
                  );
                })}
              </Row>
            </>
          )}
        </Card.Body>
      </ExpiredCard>
      <Attachments
        data={
          all_files
            ? all_files.filter(({ model_type }) => model_type === 'VaultFiscal')
            : []
        }
        canEdit={canEdit}
        isModalOpen={isAttachmentsModalOpen}
        onClick={() => toggleAttachmentsModal(true)}
        onClose={() => toggleAttachmentsModal(false)}
        modelId={id}
        modelType="VaultFiscal"
        clientId={id}
        refetch={refetch}
        maxHeight="15rem"
        disabled={!canEdit}
      />
    </>
  );
};

export default FiscalRegulatory;
